import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { useToasts } from "app/common/hooks";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { saveDraftSignalSuccess } from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";

import type { IPermanentSignal } from "../../models/core/Signal";
import type SignalFilters from "../../models/core/SignalFilters";
import type SignalId from "../../models/core/SignalId";
import { signalsQueryKeys } from "../key-factory/queryKeyFactory";

type Options<F extends SignalFilters> = Omit<
    UseMutationOptions<IPermanentSignal<F>, Error, SignalId>,
    "mutationFn" | "mutationKey"
> & {
    keyIdentifier?: string;
};

export default function useSaveDraftSignalMutation<F extends SignalFilters = SignalFilters>(options: Options<F> = {}) {
    const dispatch = useDispatch();

    const { showErrorToast, showSuccessToast } = useToasts();

    const { keyIdentifier, onSuccess, onError, ...rest } = options;

    return useMutation<IPermanentSignal, Error, SignalId>({
        mutationKey: signalsQueryKeys.lifecycleTransition(keyIdentifier),
        retry: false,
        gcTime: Number.POSITIVE_INFINITY,

        mutationFn: (signalId: SignalId) => {
            return salesApiService.signals.transitionSignalToPermanent(signalId);
        },

        onSuccess: (signal, variables, context) => {
            showSuccessToast(
                getToastItemComponent({
                    text: translate("si.signals.notification.success.save-signal-draft.text"),
                }),
            );

            dispatch(saveDraftSignalSuccess(signal));

            onSuccess?.(signal, variables, context);
        },

        onError: (error: Error, variables, context) => {
            showErrorToast(
                getToastItemComponent({
                    text: translate("si.signals.notification.error.save-signal-draft.text"),
                }),
            );

            onError?.(error, variables, context);
        },

        ...rest,
    });
}
